import React, { useEffect, useRef, useState } from 'react';
import { useLang } from './lang.js';

/**
 * Google Identity Services düğmesi.
 *
 * ÜÇ KURAL:
 *  1. İstemci kimliği SUNUCUDAN gelir (`/api/public/auth-config`), koda
 *     gömülmez: dev/prod farklı kimlik kullanıyor ve gömülü değer için
 *     yeniden derleme gerekirdi.
 *  2. Kimlik yoksa HİÇBİR ŞEY çizilmez. Düğmeyi gösterip "yapılandırılmadı"
 *     hatası vermek, kullanıcıya çalışmayan bir yol göstermek olurdu.
 *  3. Genişlik ÖLÇÜLÜR, sabit değil: GSI düğmesi bir iframe, CSS ile
 *     esnetilemiyor → sabit 280px kartın içinde dar duruyordu. Kabın
 *     genişliği izlenip düğme yeniden çizilir (GSI tavanı 400px).
 *
 * `id_token` sunucuda doğrulanır; istemcinin "ben şu kişiyim" iddiasına
 * hiçbir yerde güvenilmiyor.
 */
export default function GoogleButton({ onCredential, text = 'continue_with' }) {
  const lang = useLang();
  const [clientId, setClientId] = useState(null);
  const [width, setWidth] = useState(0);
  const ref = useRef(null);
  // Geri çağrı her çizimde yeniden bağlanmasın diye referansta tutulur:
  // `initialize` içine kapanış olarak girse, düğme her prop değişiminde
  // baştan çizilirdi.
  const cb = useRef(onCredential);
  cb.current = onCredential;

  useEffect(() => {
    fetch('/api/public/auth-config')
      .then((r) => (r.ok ? r.json() : null))
      .then((c) => setClientId(c?.googleClientId || null))
      .catch(() => setClientId(null));
  }, []);

  // Kabın genişliği: ilk ölçüm + sonraki değişimler (döndürme, pencere).
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setWidth(Math.min(400, Math.round(el.clientWidth)));
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !width) return;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.google?.accounts?.id || !ref.current) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp) => cb.current(resp.credential),
      });
      ref.current.innerHTML = '';
      // TUZAK: düğmenin dili İKİ yerden gelir — `locale` kimi sürümde yok
      // sayılıyor, betik URL'indeki `?hl=` kimi sürümde yetmiyor. İkisi de
      // verilir (bkz. reference_gis_button_locale_hl).
      window.google.accounts.id.renderButton(ref.current, {
        theme: 'outline', size: 'large', shape: 'rectangular',
        logo_alignment: 'center', width, locale: lang, text,
      });
    };

    if (window.google?.accounts?.id) { render(); return () => { cancelled = true; }; }
    const s = document.createElement('script');
    // `hl` betik YÜKLENİRKEN okunur: dil sonradan değişirse düğme eski
    // dilde kalır (arayüz metni yine güncellenir) — kabul edilebilir.
    s.src = `https://accounts.google.com/gsi/client?hl=${encodeURIComponent(lang)}`;
    s.async = true;
    s.defer = true;
    s.onload = render;
    document.head.appendChild(s);
    return () => { cancelled = true; };
  }, [clientId, lang, width, text]);

  // Kimlik gelene kadar kap yine de durur (ölçüm için) ama görünmez.
  return <div ref={ref} className="gbtn" hidden={!clientId} />;
}
